import React, { useEffect, useMemo, useState } from "react";
import adminService from "../../services/adminService";
import { pick } from "./TransportReservationUtils";
import { transportApi } from "../../services/transportService";

const AssignTransportModal = ({ reservation, onClose, onAssigned }) => {
  const reservationId = reservation?._id;

  const demandeType =
    pick(reservation, ["demandeType", "transportType", "type", "categorie"], "trajet") || "trajet";

  // demandeType (form) -> categorie (TransportTouristique)
  const categorie = useMemo(() => {
    if (demandeType === "trajet") return "trajet";
    if (demandeType === "navette") return "navette_aeroport";
    if (demandeType === "disposition") return "mise_a_disposition";
    return null;
  }, [demandeType]);

  const [transports, setTransports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransportId, setSelectedTransportId] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setSelectedTransportId("");

        if (!categorie) {
          setTransports([]);
          return;
        }

        // ✅ Fetch seulement par catégorie
        const res = await transportApi.getByCategory(categorie);

        // selon ton backend: parfois c'est res.data, parfois res.data.data, parfois res.data.items
        const list =
          res?.data?.items ||
          res?.data?.data ||
          res?.data ||
          res?.items ||
          res ||
          [];

        setTransports(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        setTransports([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [reservationId, categorie]);

  const assign = async () => {
    if (!selectedTransportId) return alert("Sélectionne un transport");

    try {
      await adminService.assignTransportToReservation(reservationId, selectedTransportId);
      alert("Transport assigné ✅");
      await onAssigned?.();
    } catch (e) {
      console.error(e);
      alert(e?.message || "Erreur lors de l'assignation");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[85] flex items-center justify-center p-4 pt-20 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold">Assigner un transport</h3>
              <p className="text-xs text-white/90">
                Filtre: demandeType={demandeType} → categorie={categorie || "—"}
              </p>
            </div>
            <button onClick={onClose} className="text-white hover:text-slate-200">
              ✖
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-slate-600">Chargement des transports...</div>
          ) : transports.length === 0 ? (
            <div className="p-6 text-center text-slate-600">
              Aucun transport trouvé pour cette catégorie ({categorie}).
            </div>
          ) : (
            <div className="space-y-3">
              {transports.map((tr) => {
                const cat = pick(tr, ["categorie"], "—");
                const dep = pick(tr, ["point_depart"], "—");
                const arr = pick(tr, ["point_arrivee"], "—");
                const cap = pick(tr, ["nb_personnes"], "—");
                const price = pick(tr, ["prix"], "—");
                const early = pick(tr, ["earlyPrice"], null);

                return (
                  <label
                    key={tr._id}
                    className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition ${
                      selectedTransportId === tr._id
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="transport"
                      checked={selectedTransportId === tr._id}
                      onChange={() => setSelectedTransportId(tr._id)}
                      className="h-4 w-4 accent-indigo-600"
                    />

                    <div className="flex-1">
                      <div className="font-bold text-slate-900">
                        {cat} • {dep} → {arr}
                      </div>
                      <div className="text-sm text-slate-600">
                        capacité {cap} • prix {price} MAD
                        {early ? ` • early ${early} MAD` : ""}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">ID: {tr._id}</div>
                    </div>

                    {tr.image && (
                      <img
                        src={
                          tr.image.startsWith("http")
                            ? tr.image
                            : `https://nouzhaback-theta.vercel.app${tr.image}`
                        }
                        alt="transport"
                        className="w-24 h-16 object-cover rounded-lg border border-slate-200"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-slate-50 px-6 py-4 flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
          >
            Fermer
          </button>
          <button
            onClick={assign}
            disabled={!selectedTransportId}
            className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            ✅ Assigner ce transport
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignTransportModal;
